import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { EventData } from './EventsContainer';
import { db, storage } from '../../../../../Config/firebase';
import { collection, doc, orderBy, query, onSnapshot, updateDoc, where, getDocs } from 'firebase/firestore';
import { Box, Checkbox, Chip, Tooltip, Tab, Tabs, Typography, CircularProgress, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { format } from 'date-fns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useAppDispatch, useAppSelector } from '../../../../../state/state';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { FLAVOR } from "../../../../../Config/firebase"
import TextFieldsIcon from '@mui/icons-material/TextFields';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import AddIcon from '@mui/icons-material/Add';
import { openModal, toggleLoading } from '../../../../../state/ModalSlice/ModalSlice';
import { setFieldsForAddEventCustomFieldsModal, setFieldsForDeleteEventCustomFieldModal, setFieldsForEditEventCustomFieldModal } from '../../../../../state/ArbitrarySlice/ArbitratySlice';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ListIcon from '@mui/icons-material/List';
import { Info } from '@mui/icons-material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useTheme } from '@mui/material/styles';
import { setFieldsForEventQRCode,  setFieldsForQRCodeSource } from '../../../../../../src/state/ArbitrarySlice/ArbitratySlice';
import DownloadIcon from '@mui/icons-material/Download';
import { Timestamp ,  addDoc } from 'firebase/firestore';
import { ExportType } from '../../../../Common/types/ExportTypes';
import { toast } from 'react-toastify';
import { ref, getDownloadURL } from 'firebase/storage';
import CampaignIcon from '@mui/icons-material/Campaign';

import { selectEffectiveUID } from '../../../../../state/UserStatesV2/userSliceV2';

type ticketType = {
  quantity: number;
  ticketPrice: number;
  ticketType: string;
}

type eventParticipant = {
  id: string;
  payeeName: string;
  payeeNumber: string;
  pdfUrl: string;
  tickets: ticketType[];
  hasAttended: boolean;
  emailId: string;
}

export type AdditionalQuestionType = {
  id: number;
  type: string;
  fieldDescription: string;
  options?: string[];
  isRequired: boolean;
}

export type AnnouncementType = {
  id: string;
  announcement: string;
  timestamp: Timestamp;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;

}



function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`event-tabpanel-${index}`}
      aria-labelledby={`event-tab-${index}`}
      className="mt-6"
      {...other}
    >
      {value === index && children}
    </div>
  );
}

// Custom styled Tab component
const StyledTab = styled(Tab)({
  minHeight: '48px',
  padding: '12px 24px',
  fontWeight: 500,
  fontSize: '14px',
  textTransform: 'none',
  '&.Mui-selected': {
    color: '#1D4ED8', // Blue color for active tab
    fontWeight: 600,
  },
});

const EventTabs = ({ event, additionalQuestions, providerId }: { event: any, additionalQuestions: AdditionalQuestionType[], providerId: string }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [announcements, setAnnouncements] = useState<AnnouncementType[]>([]);
  const [newAnnouncement, setNewAnnouncement] = useState('');
  const [isAddingAnnouncement, setIsAddingAnnouncement] = useState(false);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const dispatch = useAppDispatch()
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Fetch announcements when the announcements tab is opened
    if (newValue === 1) {
      fetchAnnouncements();
    }
  };

  const handleAddQuestion = () => {
    dispatch(setFieldsForAddEventCustomFieldsModal({ eventId: event.id }))
    dispatch(openModal("AddEventCustomFieldsModalOpen"))
  };

  const handleEditQuestion = (question: AdditionalQuestionType) => {
    
    dispatch(setFieldsForEditEventCustomFieldModal({ question: question, eventId: event.id }))
    dispatch(openModal("EditEventCustomFieldModalOpen"));
  };

  const handleDeleteClick = (question: AdditionalQuestionType) => {
    dispatch(setFieldsForDeleteEventCustomFieldModal({ questionId: question.id, eventId: event.id }))
    dispatch(openModal("DeleteAdditionalFormFieldsModalOpen"))
  }

  const fetchAnnouncements = async () => {
    try {
      const announcementsRef = collection(db, 'ServiceProviders', providerId, 'Events', event.id, 'Announcements');
      const q = query(announcementsRef, orderBy('timestamp', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedAnnouncements: AnnouncementType[] = [];
      querySnapshot.forEach((doc) => {
        fetchedAnnouncements.push({
          id: doc.id,
          ...doc.data()
        } as AnnouncementType);
      });
      
      setAnnouncements(fetchedAnnouncements);
    } catch (error) {
      console.error("Error fetching announcements:", error);
      toast.error("Failed to fetch announcements");
    }
  };

  const handleAddAnnouncement = async () => {
    if (!newAnnouncement.trim()) {
      toast.error("Announcement cannot be empty");
      return;
    }

    setIsAddingAnnouncement(true);
    try {
      const announcementsRef = collection(db, 'ServiceProviders', providerId, 'Events', event.id, 'Announcements');
      await addDoc(announcementsRef, {
        announcement: newAnnouncement.trim(),
        timestamp: Timestamp.now()
      });
      
      setNewAnnouncement('');
      setShowAnnouncementForm(false);
      toast.success("Announcement added successfully");
      // Refresh announcements list
      await fetchAnnouncements();
    } catch (error) {
      console.error("Error adding announcement:", error);
      toast.error("Failed to add announcement");
    } finally {
      setIsAddingAnnouncement(false);
    }
  };

  const handleCancelAnnouncement = () => {
    setNewAnnouncement('');
    setShowAnnouncementForm(false);
  };

  return (
    <div className="w-full">
      <Box sx={{ borderBottom: 'none' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="event tabs"
          sx={{
            minHeight: '48px',
            '& .MuiTabs-indicator': {
              height: '3px',
              borderRadius: '2px',
              backgroundColor: '#1D4ED8',
            },
            '& .MuiTabs-flexContainer': {
              gap: '16px',
            },
          }}
        >
         
          <StyledTab
            label={
              <div className="flex items-center gap-2">
                <span>Questionnaire</span>

              </div>
            }
            id="event-tab-0"
            aria-controls="event-tabpanel-0"
          />
          <StyledTab
            label={
              <div className="flex items-center gap-2">
                <CampaignIcon />
                <span>Announcements</span>
              </div>
            }
            id="event-tab-1"
            aria-controls="event-tabpanel-1"
          />
        </Tabs>
      </Box>



      <TabPanel value={activeTab} index={0} >
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <span className="flex items-center gap-2"><h3 className="text-lg font-semibold text-gray-800">Custom Questions</h3>
                  <Tooltip className='cursor-help' title="Participants will be asked these questions during registration" >
                    <Info fontSize="small" />
                  </Tooltip></span>
                <button
                  onClick={handleAddQuestion}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <AddIcon className="w-5 h-5" />
                  Add Question
                </button>
              </div>

              <div className="space-y-4">
                {additionalQuestions.map((question) => (
                  <div
                    key={question.id}
                    className="group flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-300"
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2.5 rounded-lg bg-blue-50 group-hover:bg-blue-100 transition-colors duration-300">
                        {question.type === 'textfield' ? (
                          <TextFieldsIcon className="text-blue-600" />
                        ) : question.type === 'radio' ? (
                          <RadioButtonCheckedIcon className="text-amber-600" />
                        ) : question.type === 'checkbox' ? (
                          <CheckBoxIcon className="text-green-600" />
                        ) : question.type === 'dropdown' ? (
                          <ListIcon className="text-purple-600" />
                        ) : question.type === 'location' ? (
                          <LocationOnIcon className="text-indigo-600" />
                        ) : null}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{question.fieldDescription}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Chip
                            label={question.type}
                            size="small"
                            className="bg-gray-100"
                          />
                          {question.isRequired && (
                            <Chip
                              label="Required"
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        aria-label="Edit question"
                        onClick={() => handleEditQuestion(question)}
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors duration-200"
                        aria-label="Delete question"
                        onClick={() => handleDeleteClick(question)}
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <span className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-800">Event Announcements</h3>
                  <Tooltip className='cursor-help' title="Share important updates with event participants">
                    <Info fontSize="small" />
                  </Tooltip>
                </span>
                {!showAnnouncementForm && (
                  <button
                    onClick={() => setShowAnnouncementForm(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                  >
                    <AddIcon className="w-5 h-5" />
                    New Announcement
                  </button>
                )}
              </div>

              {/* Add New Announcement Form */}
              {showAnnouncementForm && (
                <div className="mb-6 p-6 rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white shadow-lg">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CampaignIcon className="text-blue-600" />
                      <h4 className="text-md font-semibold text-gray-800">Create New Announcement</h4>
                    </div>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Type your announcement here... Share important updates with your event participants."
                      value={newAnnouncement}
                      onChange={(e) => setNewAnnouncement(e.target.value)}
                      variant="outlined"
                      sx={{
                        backgroundColor: 'white',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          '& fieldset': {
                            borderColor: '#E5E7EB',
                            borderWidth: '2px',
                          },
                          '&:hover fieldset': {
                            borderColor: '#1D4ED8',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#1D4ED8',
                            borderWidth: '2px',
                          },
                        },
                      }}
                    />
                    <div className="flex gap-3 justify-end">
                      <button
                        onClick={handleCancelAnnouncement}
                        disabled={isAddingAnnouncement}
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddAnnouncement}
                        disabled={isAddingAnnouncement || !newAnnouncement.trim()}
                        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                      >
                        {isAddingAnnouncement ? (
                          <>
                            <CircularProgress size={18} sx={{ color: 'white' }} />
                            <span>Publishing...</span>
                          </>
                        ) : (
                          <>
                            <AddIcon className="w-5 h-5" />
                            <span>Publish Announcement</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Announcements List */}
              <div className="space-y-4">
                {announcements.length > 0 ? (
                  announcements.map((announcement) => (
                    <div
                      key={announcement.id}
                      className="group p-4 rounded-xl border border-gray-200 hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-300"
                    >
                      <div className="flex items-start gap-4">
                        <div className="p-2.5 rounded-lg bg-blue-50 group-hover:bg-blue-100 transition-colors duration-300">
                          <CampaignIcon className="text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-gray-800 font-medium whitespace-pre-wrap">{announcement.announcement}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <AccessTimeIcon className="text-gray-400" fontSize="small" />
                            <span className="text-sm text-gray-500">
                              {format(new Date(announcement.timestamp.seconds * 1000), 'MMM dd, yyyy • hh:mm a')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 px-4 bg-gray-50 rounded-2xl">
                    <CampaignIcon className="text-gray-400 mb-4" sx={{ fontSize: 48 }} />
                    <p className="text-lg font-medium text-gray-600 mb-2">No Announcements Yet</p>
                    <p className="text-sm text-gray-500 text-center">
                      Start adding announcements to keep participants informed about important updates.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </TabPanel>
    </div>
  );
};

type ExportData = {
  Status: 'Pending' | 'Success' | 'Failed';
  doc_url?: string;
  RequestedOn: Timestamp;
  exportType: ExportType;
  eventId: string;
}

export default function EventDetailPage() {
  const { eventId } = useParams()
  const [event, setEvent] = useState<EventData | null>(null);
  const [participants, setParticipants] = useState<eventParticipant[]>([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [presentCount, setPresentCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [additionalQuestions, setAdditionalQuestions] = useState<AdditionalQuestionType[]>([]);
  const [isDownloading, setIsDownloading] = useState(false); // New state for download status
  const providerId = useAppSelector(selectEffectiveUID);
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const handleAction = () => {
    dispatch(openModal("QREventsandOfferModalOpen"));
    dispatch(setFieldsForQRCodeSource({ qrCodeSource: "event" }));
    dispatch(setFieldsForEventQRCode({
        eName: event?.eName || '',
        eStartTime: event?.eStartTime ? {
            seconds: event.eStartTime.seconds,
            nanoseconds: event.eStartTime.nanoseconds
        } : { seconds: 0, nanoseconds: 0 }, // Ensure it's a plain object
        address: event?.eGeoLocation?.address || '',
        eventLink: event?.eventLink || '',
    }));
  };

  const handleDownloadDetails = async () => {
    setIsDownloading(true); // Set downloading state to true
    dispatch(toggleLoading(true));
    try {
      const ExportData = {
        eventId: eventId,
        RequestedOn: Timestamp.fromDate(new Date()),
        Status: "Pending",
        exportType: ExportType.EVENT_PAYMENTS,
      };
      
      const ExportsCollectionRef = collection(db, 'ServiceProviders', providerId, 'Exports');
      const docRef = await addDoc(ExportsCollectionRef, ExportData);
      
      // Create a promise that resolves when the export is successful
      const waitForExport = new Promise<ExportData>((resolve, reject) => {
        const unsubscribe = onSnapshot(docRef, (snapshot) => {
          if (!snapshot.exists()) {
            unsubscribe();
            reject(new Error("Export document not found"));
            return;
          }

          const exportData = snapshot.data() as ExportData;
          if (exportData.Status === "Success") {
            unsubscribe();
            resolve(exportData);
          } else if (exportData.Status === "Failed") {
            unsubscribe();
            reject(new Error("Export failed"));
          }
        }, (error) => {
          unsubscribe();
          reject(error);
        });
      });

      try {
        const exportData = await waitForExport;
        if (exportData?.doc_url) {
          const filePath = decodeURIComponent(exportData.doc_url.split('/o/')[1].split('?')[0]);
          const fileRef = ref(storage, filePath);
          const downloadURL = await getDownloadURL(fileRef);

          // Create hidden anchor tag for download
          const link = document.createElement('a');
          link.href = downloadURL;
          link.download = `event_report_${eventId}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          toast.success("Event details downloaded successfully");
        } else {
          console.error("Export document is missing doc_url.");
          toast.error("Export failed to generate");
        }
      } catch (error) {
        console.error("Download error:", error);
        toast.error(`Download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error(`Download failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDownloading(false);
      dispatch(toggleLoading(false));
    }
  };


  useEffect(() => {
    const fetchEventDetails = async () => {
      if (!eventId || !providerId) return;

      try {
        setIsLoading(true);
        // Create reference to the event document
        const eventDocRef = doc(db, `/ServiceProviders/${providerId}/Events/`, eventId);

        // Set up real-time listener for the event document
        const unsubscribeEvent = onSnapshot(eventDocRef, (doc) => {
          if (!doc.exists()) {
            console.error("Event not found");
            setIsLoading(false);
            return;
          }

          setEvent({ id: doc.id, ...doc.data() } as EventData);
          if (doc.data().additionalFormFields) {
            setAdditionalQuestions(doc.data().additionalFormFields);
          }
          setIsLoading(false);
        });

        const eventAttendeesRef = collection(db, 'ServiceProviders', providerId, 'Attendees');
        const q = query(
          eventAttendeesRef,
          orderBy('createdAt', 'desc'),
          where('eventId', '==', eventId)
        );

        const unsubscribeAttendees = onSnapshot(q, (snapshot) => {
          const eventAttenteesList: eventParticipant[] = [];
          snapshot.forEach((doc: any) => {
            const docData = doc.data()
            eventAttenteesList.push({
              id: doc.id, payeeName: docData.payeeName,
              payeeNumber: docData.payeeNumber,
              pdfUrl: docData.pdfUrl,
              tickets: docData.tickets,
              hasAttended: docData.hasAttended,
              emailId: docData.emailId
            } as eventParticipant);
          });
          setParticipants(eventAttenteesList);
        }, (error) => {
          console.error("Error fetching offers: ", error);
        });

        // Return cleanup function for both listeners
        return () => {
          unsubscribeEvent();
          unsubscribeAttendees();
        };
      } catch (error) {
        console.error("Error fetching event details:", error);
        setIsLoading(false);
      }
    };
    fetchEventDetails();
  }, [eventId, providerId]);


  useEffect(() => {
    const totalRevenue = participants.reduce((sum, participant) => {
      return sum + participant.tickets.reduce((ticketSum, ticket) => {
        return ticketSum + ticket.ticketPrice * ticket.quantity;
      }, 0);
    }, 0);
    setTotalRevenue(totalRevenue);

    // Update present count to sum up tickets of attended participants
    const presentCount = participants
      .filter(participant => participant.hasAttended)
      .reduce((sum, participant) => {
        return sum + participant.tickets.reduce((ticketSum, ticket) => {
          return ticketSum + ticket.quantity;
        }, 0);
      }, 0);
    setPresentCount(presentCount);
  }, [participants]);

  const getTotalTickets = () => {
    return participants.reduce((sum, participant) => {
      return sum + participant.tickets.reduce((ticketSum, ticket) => {
        return ticketSum + ticket.quantity;
      }, 0);
    }, 0);
  };

  const handleAttendanceChange = async (participantId: string, isPresent: boolean) => {
    const eventAttendeesRef = collection(db, 'ServiceProviders', providerId, 'Attendees');
    const docRef = doc(eventAttendeesRef, participantId);

    const updateData = {
      hasAttended: isPresent,
      ...(isPresent && { availedTime: new Date() })
    };

    await updateDoc(docRef, updateData);
  };

  const filteredParticipants = participants.filter(participant =>
    participant.payeeName.toLowerCase().includes(searchQuery.toLowerCase())
  );



  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-cc_brand border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600 font-medium">Loading event details...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="text-center">
            <p className="text-xl text-gray-800 font-medium">Event Not Found</p>
            <p className="text-gray-600 mt-2">The event you're looking for doesn't exist or has been removed.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-[1px] border-t-cc_brand border-t-4 border-gray-300 h-[100%] w-[100%] bg-white shadow-xl flex gap-2 p-2 rounded-xl"
      style={{ boxShadow: "0 0px 6px 0px rgba(0, 0, 0, 0.1), 0 0px 6px 0px rgba(0, 0, 0, 0.06)" }}>

      {/* Loader for download */}
      {isDownloading && (
        <div className="fixed inset-0 flex flex-col items-center justify-center z-50 bg-white bg-opacity-50">
          <CircularProgress size={40} sx={{ color: '#4285F4', mb: 3 }} />
          <div className="text-lg font-medium text-black">
            Don't refresh until data is being downloaded...
          </div>
        </div>
      )}

      {/* Left Section */}
      <div className="box-left h-full w-[40%] bg-white shadow-xl border-r-[1px] border-cc_modal-grey-border rounded-xl flex flex-col">
        {/* Event Header with Gradient Overlay */}
        <div className="relative h-64 shrink-0 rounded-t-xl overflow-hidden">
          <img
            src={event.ePromoImage}
            alt={event.eName}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="flex justify-between items-center mb-2">
              <h1 className="text-2xl font-bold tracking-tight">{event.eName}</h1>

            </div>

            {/* Event Meta Information */}
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center gap-2">
                <CalendarTodayIcon fontSize="small" />
                <span>{format(new Date(event.eStartTime?.seconds * 1000), 'MMM dd, yyyy')}</span>
              </div>
              <div className="flex items-center gap-2">
                <AccessTimeIcon fontSize="small" />
                <span>{format(new Date(event.eStartTime?.seconds * 1000), 'hh:mm a')}</span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <div>
                  <LocationOnIcon fontSize="small" />
                  <span className="truncate max-w-[75%] overflow-hidden text-ellipsis">{event.placeName}</span>
                </div>
                <div className=" ">
                  <Box sx={{ display: "flex", gap: 2, mt: 1, position: 'absolute', top: -70, right: 16 }}>
                    <Tooltip title="Get your QR Code" arrow>
                      <Box
                        onClick={() => handleAction()}
                        sx={{
                          cursor: "pointer",
                          bgcolor: theme.palette.background.paper,
                          color: "Black",
                          py: 0.5,
                          px: 2,
                          borderRadius: 20,
                          display: "flex",
                          alignItems: "center",
                          backdropFilter: 'blur(4px)', // Apply backdrop blur
                          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)', // Optional: add shadow for depth
                          transition: "all 0.3s",
                          "&:hover": {
                            // bgcolor: theme.palette.primary.main,
                            transform: "scale(1.03)",
                          },
                        }}
                      >
                        <QrCode2Icon sx={{ mr: 1, fontSize: 18 }} />
                        <Typography variant="body2" sx={{ fontSize: 15 }}>
                          Get QR Code
                        </Typography>
                      </Box>
                    </Tooltip>

                    <Tooltip title={"Download event details"} arrow>
                      <Box
                        onClick={async () => {
                          try {
                              await handleDownloadDetails();
                              // toast.success("Event details exported successfully");
                             
                            } catch (error) {
                              // toast.error("Failed to export event details");
                              console.log("Failed to export event details");
                            }
                          }
                        }
                        sx={{
                          cursor: "pointer",
                          bgcolor: theme.palette.background.paper,
                          color: "Black",
                          py: 0.5,
                          px: 2,
                          borderRadius: 20,
                          display: "flex",
                          alignItems: "center",
                          backdropFilter: 'blur(4px)',
                          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                          transition: "all 0.3s",
                          "&:hover": {
                            transform: "scale(1.03)",
                          },
                        }}
                      >
                        {isLoading ? (
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                        ) : (
                          <DownloadIcon sx={{ mr: 1, fontSize: 18 }} />
                        )}
                        <Typography variant="body2" sx={{ fontSize: 15 }}>
                          {isLoading ? "Exporting..." : "Download Participants Details"}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                </div>
                <div className="bg-white/20 backdrop-blur-sm px-2 py-1.5 rounded-full cursor-pointer" onClick={() => {
                  if (FLAVOR === "DEVELOPMENT") {
                    window.open(`https://dev.goplundr.com/details/${providerId}/event/${eventId}`, '_blank');
                    }
                    else if (FLAVOR === "PRODUCTION") {
                    window.open(`https://www.goplundr.com/details/${providerId}/event/${eventId}`, '_blank');
                    }
                 }}>
                  <span className="text-sm font-semibold">View Event Page <ArrowOutwardIcon fontSize="small" /></span>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-2xl">
                <div className="flex flex-col items-center">
                  <div className="bg-blue-500/10 p-2 rounded-full mb-2">
                    <CurrencyRupeeIcon className="text-blue-600" />
                  </div>
                  <p className="text-lg font-bold text-blue-600">₹{totalRevenue}</p>
                  <p className="text-sm text-blue-600/70">Revenue</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-2xl">
                <div className="flex flex-col items-center">
                  <div className="bg-green-500/10 p-2 rounded-full mb-2">
                    <PeopleAltIcon className="text-green-600" />
                  </div>
                  <p className="text-lg font-bold text-green-600">{getTotalTickets()}</p>
                  <p className="text-sm text-green-600/70">Total Participants</p>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-2xl">
                <div className="flex flex-col items-center">
                  <div className="bg-purple-500/10 p-2 rounded-full mb-2">
                    <CheckCircleIcon className="text-purple-600" />
                  </div>
                  <p className="text-lg font-bold text-purple-600">{presentCount}</p>
                  <p className="text-sm text-purple-600/70">Present</p>
                </div>
              </div>
            </div>

            {/* Event Description */}
            <EventTabs event={event} additionalQuestions={additionalQuestions} providerId={providerId} />
          </div>
        </div>
      </div>

      {/* Right Section */}
      <div className="w-[60%] h-full">
        <div className="p-6">
          {/* Header with Search */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-800">Participants</h2>
              <p className="text-sm text-gray-500 mt-1">
                {participants.length} registered attendees
              </p>
            </div>
            <div className="relative w-72">
              <input
                type="text"
                placeholder="Search participants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2.5 pl-11 rounded-xl border border-gray-200 focus:outline-none focus:border-cc_brand focus:ring-1 focus:ring-cc_brand text-sm transition-all duration-200"
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </div>
          </div>

          {/* Participants List */}
          <div className="space-y-3 max-h-[calc(100vh-220px)] overflow-y-auto diff-scrollbar pr-2">
            {filteredParticipants.length > 0 ? (
              filteredParticipants.map((participant) => (
                <div
                  key={participant.id}
                  className="group bg-white p-2.5 border-[1px]  rounded-xl  border-gray-300 hover:shadow-lg hover:border-cc_brand/30 transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-gradient-to-br from-gray-100 to-gray-200 p-3 rounded-xl group-hover:scale-110 transition-transform duration-300">
                        <PersonOutlineIcon className="text-gray-700" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800 group-hover:text-cc_brand transition-colors duration-300">
                          {participant.payeeName}
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-sm text-gray-500">{participant.payeeNumber}</span>
                          {participant.emailId && <span className="w-1 h-1 bg-gray-300 rounded-full"></span>}
                          <span className="text-sm text-gray-500">{participant.emailId}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {participant.tickets.map((ticket) => {
                        return (<Chip
                          key={ticket.ticketType}
                          label={`${ticket.ticketType} - ${ticket.quantity}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          className="bg-blue-50 hover:bg-blue-100 transition-colors duration-200"
                        />)
                      })}

                      <Tooltip title="Mark Attendance">
                        <div className="relative">
                          <Checkbox
                            checked={participant.hasAttended}
                            onChange={(e) => handleAttendanceChange(participant.id, e.target.checked)}
                            color="success"
                            className="rounded-full transition-transform duration-200 hover:scale-110"
                          />
                          {participant.hasAttended && (
                            <div className="absolute -top-2 -right-2 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                          )}
                        </div></Tooltip>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-16 px-4 bg-gray-50 rounded-2xl">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                  strokeLinejoin="round" className="text-gray-400 mb-4">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                <p className="text-lg font-medium text-gray-600 mb-2">No Participants Yet</p>
                <p className="text-sm text-gray-500 text-center">
                  Start adding participants to track attendance and manage your event.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}